import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
  Collapse,
  Divider,
  Button,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Games as GamesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useGetServiceCodesQuery } from '../../store/api/dashboardApi';

// --- Helper Functions & Components ---

const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const ProductStatus = ({ serviceCode, betCount }) => {
  if (serviceCode === 'all') {
    return <Chip label="All Services" color="info" size="small" />;
  }
  const numBets = parseInt(betCount, 10) || 0;
  if (numBets === 0) return <Chip label="Inactive" color="default" size="small" />;
  if (numBets < 10) return <Chip label="Low Activity" color="warning" size="small" />;
  if (numBets < 100) return <Chip label="Active" color="success" size="small" />;
  return <Chip label="High Activity" color="primary" size="small" />;
};

const ProductCard = ({ product, onToggle, isExpanded }) => {
  const theme = useTheme();

  const StatItem = ({ icon, label, value, color = 'primary' }) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ 
        color: theme.palette[color].main, 
        display: 'flex', 
        fontSize: { xs: '1.2rem', md: '1.5rem' } 
      }}>
        {icon}
      </Box>
      <Box>
        <Typography 
          component="div" 
          sx={{ 
            color: theme.palette[color].main, 
            fontWeight: 'bold', 
            fontSize: { xs: '1rem', md: '1.25rem' } 
          }}
        >
          {value}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
        >
          {label}
        </Typography>
      </Box>
    </Stack>
  );

  // Get appropriate background color based on product status
  const getCardColors = () => {
    const numBets = parseInt(product.betCount, 10) || 0;
    if (product.serviceCode === 'all') {
      return { 
        bgColor: theme.palette.info.light, 
        borderColor: alpha(theme.palette.info.light, 0.4) 
      };
    }
    if (numBets === 0) {
      return { 
        bgColor: theme.palette.grey[300], 
        borderColor: alpha(theme.palette.grey[300], 0.4) 
      };
    }
    if (numBets < 10) {
      return { 
        bgColor: theme.palette.warning.light, 
        borderColor: alpha(theme.palette.warning.light, 0.4) 
      };
    }
    if (numBets < 100) {
      return { 
        bgColor: theme.palette.success.light, 
        borderColor: alpha(theme.palette.success.light, 0.4) 
      };
    }
    return { 
      bgColor: theme.palette.primary.light, 
      borderColor: alpha(theme.palette.primary.light, 0.4) 
    };
  };

  const { bgColor, borderColor } = getCardColors();

  return (
    <Card sx={{ 
      mb: 2,
      height: '100%', 
      background: `linear-gradient(135deg, ${alpha(bgColor, 0.2)} 0%, ${alpha(bgColor, 0.1)} 100%)`, 
      border: `1px solid ${borderColor}`, 
      transition: 'transform 0.2s, box-shadow 0.2s', 
      '&:hover': { 
        transform: 'translateY(-2px)', 
        boxShadow: theme.shadows[8] 
      } 
    }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {product.productName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {product.serviceCode}
            </Typography>
          </Box>
          <ProductStatus serviceCode={product.serviceCode} betCount={product.betCount} />
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatItem 
              icon={<GamesIcon />} 
              label="Total Bets" 
              value={formatNumber(product.betCount)} 
              color="primary" 
            />
          </Grid>
          <Grid item xs={6}>
            <StatItem 
              icon={<TrendingUpIcon />} 
              label="Total Revenue" 
              value={formatCurrency(product.totalAmount)} 
              color="success" 
            />
          </Grid>
        </Grid>
        
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatItem 
                  icon={<CategoryIcon />} 
                  label="Category" 
                  value={product.category} 
                  color="secondary" 
                />
              </Grid>
              <Grid item xs={6}>
                <StatItem 
                  icon={<MoneyIcon />} 
                  label="Avg. Bet Amount" 
                  value={formatCurrency(product.avgBetAmount)} 
                  color="warning" 
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
      <Divider />
      <Button
        fullWidth
        onClick={() => onToggle(product.id)}
        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ 
          py: 1, 
          color: 'text.secondary', 
          textTransform: 'none', 
          fontWeight: 'normal',
          backgroundColor: alpha(theme.palette.background.default, 0.6),
          '&:hover': { 
            backgroundColor: alpha(theme.palette.primary.light, 0.15) 
          }
        }}
      >
        {isExpanded ? 'Less details' : 'More details'}
      </Button>
    </Card>
  );
};


// --- Main Page Component ---

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [expandedCards, setExpandedCards] = useState(new Set());

  const handleCardToggle = (cardId) => {
    setExpandedCards(prev => {
      const newExpanded = new Set(prev);
      newExpanded.has(cardId) ? newExpanded.delete(cardId) : newExpanded.add(cardId);
      return newExpanded;
    });
  };

  const { data: serviceCodesData = [], isLoading, error, refetch } = useGetServiceCodesQuery();

  const filteredData = useMemo(() => {
    if (!Array.isArray(serviceCodesData)) return [];
    return serviceCodesData
      .filter(item => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
          item.label?.toLowerCase().includes(search) ||
          item.value?.toLowerCase().includes(search)
        );
      })
      .map(item => ({
        id: item.value || item.label,
        serviceCode: item.value || 'N/A',
        productName: item.label || 'Unknown Product',
        betCount: parseInt(item.bet_count, 10) || 0,
        totalAmount: parseFloat(item.total_amount) || 0,
        status: item.value === 'all' ? 'system' : 'active',
        category: item.value === 'all' ? 'System' : 'Game Service',
        avgBetAmount: (parseInt(item.bet_count, 10) || 0) > 0 ? (parseFloat(item.total_amount) / parseInt(item.bet_count, 10)) : 0,
      }));
  }, [serviceCodesData, searchTerm]);

  const summaryStats = useMemo(() => {
    const activeProducts = filteredData.filter(p => p.serviceCode !== 'all');
    return {
      totalProducts: activeProducts.length,
      totalBets: activeProducts.reduce((sum, p) => sum + p.betCount, 0),
      totalRevenue: activeProducts.reduce((sum, p) => sum + p.totalAmount, 0),
      avgRevenue: activeProducts.length > 0 ? activeProducts.reduce((sum, p) => sum + p.totalAmount, 0) / activeProducts.length : 0,
    };
  }, [filteredData]);
  
  const columns = [
    { field: 'serviceCode', headerName: 'Service Code', width: 150, renderCell: (params) => <Box sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{params.value}</Box> },
    { field: 'productName', headerName: 'Product Name', flex: 1, minWidth: 200 },
    { field: 'category', headerName: 'Category', width: 120 },
    { field: 'betCount', headerName: 'Total Games', width: 120, type: 'number', renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GamesIcon fontSize="small" color="action" />{formatNumber(params.value)}</Box> },
    { field: 'totalAmount', headerName: 'Total Play Amount', width: 150, type: 'number', renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}><MoneyIcon fontSize="small" />{formatCurrency(params.value)}</Box> },
    { field: 'avgBetAmount', headerName: 'Avg Play Amount', width: 150, type: 'number', renderCell: (params) => formatCurrency(params.value) },
    { field: 'status', headerName: 'Status', width: 130, renderCell: (params) => <ProductStatus serviceCode={params.row.serviceCode} betCount={params.row.betCount} /> },
  ];

  // Summary cards configuration matching customer cards style
  const summaryCards = [
    { 
      title: 'Active Products', 
      value: summaryStats.totalProducts.toLocaleString(), 
      color: 'primary.main', 
      icon: <GamesIcon />, 
      bgColor: theme.palette.primary.light 
    },
    { 
      title: 'Total Bets', 
      value: formatNumber(summaryStats.totalBets), 
      color: 'success.main', 
      icon: <TrendingUpIcon />, 
      bgColor: theme.palette.success.light 
    },
    { 
      title: 'Total Play Amount', 
      value: formatCurrency(summaryStats.totalRevenue), 
      color: 'info.main', 
      icon: <MoneyIcon />, 
      bgColor: theme.palette.info.light 
    },
    { 
      title: 'Avg PlayAmount/Product', 
      value: formatCurrency(summaryStats.avgRevenue), 
      color: 'warning.main', 
      icon: <MoneyIcon />, 
      bgColor: theme.palette.warning.light 
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* --- Header --- */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Products & Services
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and monitor your gaming products and service codes
        </Typography>
      </Box>

      {/* --- Summary Cards (Updated to match customer cards style) --- */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <Card sx={{ 
              height: '100%', 
              background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
              border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
              transition: 'transform 0.2s, box-shadow 0.2s', 
              '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{card.icon}</Box>
                  <Typography component="div" sx={{ color: card.color, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>{card.value}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{card.title}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* --- Controls --- */}
      <Card sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: { xs: 2, md: 3 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s', 
        '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField 
            fullWidth 
            placeholder="Search products or codes..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            size="small" 
            InputProps={{ 
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
              }
            }}
          />
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load products data. Please try refreshing.</Alert>}
      
      {/* --- Main Content --- */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : isMobile ? (
        <Box>
          {filteredData.length > 0 ? (
            filteredData.map((product) => (
              <ProductCard key={product.id} product={product} onToggle={handleCardToggle} isExpanded={expandedCards.has(product.id)} />
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', background: 'transparent', boxShadow: 'none' }}><Typography>No products found.</Typography></Paper>
          )}
        </Box>
      ) : (
        <Card>
          <DataGrid
            rows={filteredData}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            sx={{ 
              border: 'none',
              '& .MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider' },
              '& .MuiDataGrid-columnHeaders': { 
                backgroundColor: alpha(theme.palette.primary.light, 0.1), 
                borderBottom: '2px solid', 
                borderBottomColor: 'divider' 
              },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
              '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: alpha(theme.palette.action.hover, 0.5) },
            }}
          />
        </Card>
      )}

      {/* --- Footer --- */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredData.length} products
        </Typography>
      </Box>
    </Box>
  );
};

export default Products;

// import React, { useState, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   Paper,
//   Card,
//   CardContent,
//   Grid,
//   Chip,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Tooltip,
//   Alert,
//   CircularProgress,
//   Stack,
//   useTheme,
//   useMediaQuery,
//   Collapse,
//   Divider,
//   Button,
//   Avatar,
//   alpha,
// } from '@mui/material';
// import {
//   Search as SearchIcon,
//   Refresh as RefreshIcon,
//   TrendingUp as TrendingUpIcon,
//   AttachMoney as MoneyIcon,
//   Games as GamesIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
//   Category as CategoryIcon,
// } from '@mui/icons-material';
// import { DataGrid } from '@mui/x-data-grid';
// import { useGetServiceCodesQuery } from '../../store/api/dashboardApi';

// // --- Helper Functions & Components ---

// const formatNumber = (num) => {
//   if (num === undefined || num === null) return '0';
//   if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
//   if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
//   return num.toString();
// };

// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat('en-KE', {
//     style: 'currency',
//     currency: 'KES',
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   }).format(amount || 0);
// };

// const ProductStatus = ({ serviceCode, betCount }) => {
//   if (serviceCode === 'all') {
//     return <Chip label="All Services" color="info" size="small" />;
//   }
//   const numBets = parseInt(betCount, 10) || 0;
//   if (numBets === 0) return <Chip label="Inactive" color="default" size="small" />;
//   if (numBets < 10) return <Chip label="Low Activity" color="warning" size="small" />;
//   if (numBets < 100) return <Chip label="Active" color="success" size="small" />;
//   return <Chip label="High Activity" color="primary" size="small" />;
// };

// const ProductCard = ({ product, onToggle, isExpanded }) => {
//   const theme = useTheme();

//   const StatItem = ({ icon, label, value, color = 'primary' }) => (
//     <Stack direction="row" spacing={1.5} alignItems="center">
//       <Avatar sx={{ bgcolor: alpha(theme.palette[color].main, 0.1), color: theme.palette[color].main }}>
//         {icon}
//       </Avatar>
//       <Box>
//         <Typography variant="body2" color="text.secondary">{label}</Typography>
//         <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value}</Typography>
//       </Box>
//     </Stack>
//   );

//   return (
//     <Card sx={{ mb: 2 }}>
//       <CardContent>
//         <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
//           <Box>
//             <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
//               {product.productName}
//             </Typography>
//             <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
//               {product.serviceCode}
//             </Typography>
//           </Box>
//           <ProductStatus serviceCode={product.serviceCode} betCount={product.betCount} />
//         </Stack>

//         <Stack spacing={2}>
//           <StatItem icon={<GamesIcon />} label="Total Bets" value={formatNumber(product.betCount)} color="primary" />
//           <StatItem icon={<TrendingUpIcon />} label="Total Revenue" value={formatCurrency(product.totalAmount)} color="success" />
//         </Stack>
        
//         <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//           <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${theme.palette.divider}` }}>
//             <Stack spacing={2}>
//               <StatItem icon={<CategoryIcon />} label="Category" value={product.category} color="secondary" />
//               <StatItem icon={<MoneyIcon />} label="Avg. Bet Amount" value={formatCurrency(product.avgBetAmount)} color="warning" />
//             </Stack>
//           </Box>
//         </Collapse>
//       </CardContent>
//       <Divider />
//       <Button
//         fullWidth
//         onClick={() => onToggle(product.id)}
//         endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{ py: 1, color: 'text.secondary', textTransform: 'none', fontWeight: 'normal' }}
//       >
//         {isExpanded ? 'Less details' : 'More details'}
//       </Button>
//     </Card>
//   );
// };


// // --- Main Page Component ---

// const Products = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const [searchTerm, setSearchTerm] = useState('');
//   const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
//   const [expandedCards, setExpandedCards] = useState(new Set());

//   const handleCardToggle = (cardId) => {
//     setExpandedCards(prev => {
//       const newExpanded = new Set(prev);
//       newExpanded.has(cardId) ? newExpanded.delete(cardId) : newExpanded.add(cardId);
//       return newExpanded;
//     });
//   };

//   const { data: serviceCodesData = [], isLoading, error, refetch } = useGetServiceCodesQuery();

//   const filteredData = useMemo(() => {
//     if (!Array.isArray(serviceCodesData)) return [];
//     return serviceCodesData
//       .filter(item => {
//         if (!searchTerm) return true;
//         const search = searchTerm.toLowerCase();
//         return (
//           item.label?.toLowerCase().includes(search) ||
//           item.value?.toLowerCase().includes(search)
//         );
//       })
//       .map(item => ({
//         id: item.value || item.label,
//         serviceCode: item.value || 'N/A',
//         productName: item.label || 'Unknown Product',
//         betCount: parseInt(item.bet_count, 10) || 0,
//         totalAmount: parseFloat(item.total_amount) || 0,
//         status: item.value === 'all' ? 'system' : 'active',
//         category: item.value === 'all' ? 'System' : 'Game Service',
//         avgBetAmount: (parseInt(item.bet_count, 10) || 0) > 0 ? (parseFloat(item.total_amount) / parseInt(item.bet_count, 10)) : 0,
//       }));
//   }, [serviceCodesData, searchTerm]);

//   const summaryStats = useMemo(() => {
//     const activeProducts = filteredData.filter(p => p.serviceCode !== 'all');
//     return {
//       totalProducts: activeProducts.length,
//       totalBets: activeProducts.reduce((sum, p) => sum + p.betCount, 0),
//       totalRevenue: activeProducts.reduce((sum, p) => sum + p.totalAmount, 0),
//       avgRevenue: activeProducts.length > 0 ? activeProducts.reduce((sum, p) => sum + p.totalAmount, 0) / activeProducts.length : 0,
//     };
//   }, [filteredData]);
  
//   const columns = [
//     { field: 'serviceCode', headerName: 'Service Code', width: 150, renderCell: (params) => <Box sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{params.value}</Box> },
//     { field: 'productName', headerName: 'Product Name', flex: 1, minWidth: 200 },
//     { field: 'category', headerName: 'Category', width: 120 },
//     { field: 'betCount', headerName: 'Total Games', width: 120, type: 'number', renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GamesIcon fontSize="small" color="action" />{formatNumber(params.value)}</Box> },
//     { field: 'totalAmount', headerName: 'Total Play Amount', width: 150, type: 'number', renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}><MoneyIcon fontSize="small" />{formatCurrency(params.value)}</Box> },
//     { field: 'avgBetAmount', headerName: 'Avg Play Amount', width: 150, type: 'number', renderCell: (params) => formatCurrency(params.value) },
//     { field: 'status', headerName: 'Status', width: 130, renderCell: (params) => <ProductStatus serviceCode={params.row.serviceCode} betCount={params.row.betCount} /> },
//   ];

//   return (
//     <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//       {/* --- Header --- */}
//       <Box sx={{ mb: { xs: 2, md: 3 } }}>
//         <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//           Products & Services
//         </Typography>
//         <Typography variant="body1" color="text.secondary">
//           Manage and monitor your gaming products and service codes
//         </Typography>
//       </Box>

//       {/* --- Summary Cards (Icons Now High-Contrast) --- */}
//       <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 48, height: 48 }}>
//                     <GamesIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{summaryStats.totalProducts}</Typography>
//                     <Typography variant="body2" color="text.secondary">Active Products</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'success.main', color: 'success.contrastText', width: 48, height: 48 }}>
//                     <TrendingUpIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatNumber(summaryStats.totalBets)}</Typography>
//                     <Typography variant="body2" color="text.secondary">Total Bets</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'info.main', color: 'info.contrastText', width: 48, height: 48 }}>
//                     <MoneyIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(summaryStats.totalRevenue)}</Typography>
//                     <Typography variant="body2" color="text.secondary">Total Play Amount</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', width: 48, height: 48 }}>
//                     <MoneyIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(summaryStats.avgRevenue)}</Typography>
//                     <Typography variant="body2" color="text.secondary">Avg PlayAmount/Product</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//       </Grid>
      
//       {/* --- Controls --- */}
//       <Card sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
//         <Stack direction="row" spacing={1.5} alignItems="center">
//           <TextField fullWidth placeholder="Search products or codes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)}}/>
//           <Tooltip title="Refresh Data"><IconButton onClick={() => refetch()} disabled={isLoading}><RefreshIcon /></IconButton></Tooltip>
//         </Stack>
//       </Card>

//       {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load products data. Please try refreshing.</Alert>}
      
//       {/* --- Main Content --- */}
//       {isLoading ? (
//         <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
//       ) : isMobile ? (
//         <Box>
//           {filteredData.length > 0 ? (
//             filteredData.map((product) => (
//               <ProductCard key={product.id} product={product} onToggle={handleCardToggle} isExpanded={expandedCards.has(product.id)} />
//             ))
//           ) : (
//             <Paper sx={{ p: 4, textAlign: 'center', background: 'transparent', boxShadow: 'none' }}><Typography>No products found.</Typography></Paper>
//           )}
//         </Box>
//       ) : (
//         <Card>
//           <DataGrid
//             rows={filteredData}
//             columns={columns}
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={[25, 50, 100]}
//             disableRowSelectionOnClick
//             autoHeight
//             sx={{ border: 'none' }}
//           />
//         </Card>
//       )}

//       {/* --- Footer --- */}
//       <Box sx={{ mt: 2, textAlign: 'center' }}>
//         <Typography variant="body2" color="text.secondary">
//           Showing {filteredData.length} products
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// export default Products;

// import React, { useState, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   Paper,
//   Card,
//   CardContent,
//   Grid,
//   Chip,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Tooltip,
//   Alert,
//   CircularProgress,
//   Stack,
//   useTheme,
//   useMediaQuery,
//   Collapse,
//   Divider,
//   Button,
//   Avatar,
//   alpha,
// } from '@mui/material';
// import {
//   Search as SearchIcon,
//   Refresh as RefreshIcon,
//   TrendingUp as TrendingUpIcon,
//   AttachMoney as MoneyIcon,
//   Games as GamesIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
//   Category as CategoryIcon,
// } from '@mui/icons-material';
// import { DataGrid } from '@mui/x-data-grid';
// import { useGetServiceCodesQuery } from '../../store/api/dashboardApi';

// // --- Helper Functions & Components ---

// const formatNumber = (num) => {
//   if (num === undefined || num === null) return '0';
//   if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
//   if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
//   return num.toString();
// };

// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat('en-KE', {
//     style: 'currency',
//     currency: 'KES',
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   }).format(amount || 0);
// };

// const ProductStatus = ({ serviceCode, betCount }) => {
//   if (serviceCode === 'all') {
//     return <Chip label="All Services" color="info" size="small" />;
//   }
//   const numBets = parseInt(betCount, 10) || 0;
//   if (numBets === 0) return <Chip label="Inactive" color="default" size="small" />;
//   if (numBets < 10) return <Chip label="Low Activity" color="warning" size="small" />;
//   if (numBets < 100) return <Chip label="Active" color="success" size="small" />;
//   return <Chip label="High Activity" color="primary" size="small" />;
// };

// const ProductCard = ({ product, onToggle, isExpanded }) => {
//   const theme = useTheme();

//   const StatItem = ({ icon, label, value, color = 'primary' }) => (
//     <Stack direction="row" spacing={1.5} alignItems="center">
//       <Avatar sx={{ bgcolor: alpha(theme.palette[color].main, 0.1), color: theme.palette[color].main }}>
//         {icon}
//       </Avatar>
//       <Box>
//         <Typography variant="body2" color="text.secondary">{label}</Typography>
//         <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value}</Typography>
//       </Box>
//     </Stack>
//   );

//   return (
//     <Card sx={{ mb: 2 }}>
//       <CardContent>
//         <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
//           <Box>
//             <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
//               {product.productName}
//             </Typography>
//             <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
//               {product.serviceCode}
//             </Typography>
//           </Box>
//           <ProductStatus serviceCode={product.serviceCode} betCount={product.betCount} />
//         </Stack>

//         <Stack spacing={2}>
//           <StatItem icon={<GamesIcon />} label="Total Bets" value={formatNumber(product.betCount)} color="primary" />
//           <StatItem icon={<TrendingUpIcon />} label="Total Revenue" value={formatCurrency(product.totalAmount)} color="success" />
//         </Stack>
        
//         <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//           <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${theme.palette.divider}` }}>
//             <Stack spacing={2}>
//               <StatItem icon={<CategoryIcon />} label="Category" value={product.category} color="secondary" />
//               <StatItem icon={<MoneyIcon />} label="Avg. Bet Amount" value={formatCurrency(product.avgBetAmount)} color="warning" />
//             </Stack>
//           </Box>
//         </Collapse>
//       </CardContent>
//       <Divider />
//       <Button
//         fullWidth
//         onClick={() => onToggle(product.id)}
//         endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{ py: 1, color: 'text.secondary', textTransform: 'none', fontWeight: 'normal' }}
//       >
//         {isExpanded ? 'Less details' : 'More details'}
//       </Button>
//     </Card>
//   );
// };


// // --- Main Page Component ---

// const Products = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const [searchTerm, setSearchTerm] = useState('');
//   const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
//   const [expandedCards, setExpandedCards] = useState(new Set());

//   const handleCardToggle = (cardId) => {
//     setExpandedCards(prev => {
//       const newExpanded = new Set(prev);
//       newExpanded.has(cardId) ? newExpanded.delete(cardId) : newExpanded.add(cardId);
//       return newExpanded;
//     });
//   };

//   const { data: serviceCodesData = [], isLoading, error, refetch } = useGetServiceCodesQuery();

//   const filteredData = useMemo(() => {
//     if (!Array.isArray(serviceCodesData)) return [];
//     return serviceCodesData
//       .filter(item => {
//         if (!searchTerm) return true;
//         const search = searchTerm.toLowerCase();
//         return (
//           item.label?.toLowerCase().includes(search) ||
//           item.value?.toLowerCase().includes(search)
//         );
//       })
//       .map(item => ({
//         id: item.value || item.label,
//         serviceCode: item.value || 'N/A',
//         productName: item.label || 'Unknown Product',
//         betCount: parseInt(item.bet_count, 10) || 0,
//         totalAmount: parseFloat(item.total_amount) || 0,
//         status: item.value === 'all' ? 'system' : 'active',
//         category: item.value === 'all' ? 'System' : 'Game Service',
//         avgBetAmount: (parseInt(item.bet_count, 10) || 0) > 0 ? (parseFloat(item.total_amount) / parseInt(item.bet_count, 10)) : 0,
//       }));
//   }, [serviceCodesData, searchTerm]);

//   const summaryStats = useMemo(() => {
//     const activeProducts = filteredData.filter(p => p.serviceCode !== 'all');
//     return {
//       totalProducts: activeProducts.length,
//       totalBets: activeProducts.reduce((sum, p) => sum + p.betCount, 0),
//       totalRevenue: activeProducts.reduce((sum, p) => sum + p.totalAmount, 0),
//       avgRevenue: activeProducts.length > 0 ? activeProducts.reduce((sum, p) => sum + p.totalAmount, 0) / activeProducts.length : 0,
//     };
//   }, [filteredData]);
  
//   const columns = [
//     { field: 'serviceCode', headerName: 'Service Code', width: 150, renderCell: (params) => <Box sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{params.value}</Box> },
//     { field: 'productName', headerName: 'Product Name', flex: 1, minWidth: 200 },
//     { field: 'category', headerName: 'Category', width: 120 },
//     { field: 'betCount', headerName: 'Total Games', width: 120, type: 'number', renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GamesIcon fontSize="small" color="action" />{formatNumber(params.value)}</Box> },
//     { field: 'totalAmount', headerName: 'Total Play Amount', width: 150, type: 'number', renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}><MoneyIcon fontSize="small" />{formatCurrency(params.value)}</Box> },
//     { field: 'avgBetAmount', headerName: 'Avg Play Amount', width: 150, type: 'number', renderCell: (params) => formatCurrency(params.value) },
//     { field: 'status', headerName: 'Status', width: 130, renderCell: (params) => <ProductStatus serviceCode={params.row.serviceCode} betCount={params.row.betCount} /> },
//   ];

//   return (
//     <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//       {/* --- Header --- */}
//       <Box sx={{ mb: { xs: 2, md: 3 } }}>
//         <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//           Products & Services
//         </Typography>
//         <Typography variant="body1" color="text.secondary">
//           Manage and monitor your gaming products and service codes
//         </Typography>
//       </Box>

//       {/* --- Summary Cards (Icons Now High-Contrast) --- */}
//       <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 48, height: 48 }}>
//                     <GamesIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{summaryStats.totalProducts}</Typography>
//                     <Typography variant="body2" color="text.secondary">Active Products</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//            <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'success.main', color: 'success.contrastText', width: 48, height: 48 }}>
//                     <TrendingUpIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatNumber(summaryStats.totalBets)}</Typography>
//                     <Typography variant="body2" color="text.secondary">Total Bets</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//            <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'info.main', color: 'info.contrastText', width: 48, height: 48 }}>
//                     <MoneyIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(summaryStats.totalRevenue)}</Typography>
//                     <Typography variant="body2" color="text.secondary">Total Play Amount</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//            <Card>
//             <CardContent>
//               <Stack direction="row" spacing={2} alignItems="center">
//                 <Avatar sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', width: 48, height: 48 }}>
//                     <MoneyIcon />
//                 </Avatar>
//                 <Box>
//                     <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(summaryStats.avgRevenue)}</Typography>
//                     <Typography variant="body2" color="text.secondary">Avg PlayAmount/Product</Typography>
//                 </Box>
//               </Stack>
//             </CardContent>
//           </Card>
//         </Grid>
//       </Grid>
      
//       {/* --- Controls --- */}
//       <Card sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
//         <Stack direction="row" spacing={1.5} alignItems="center">
//           <TextField fullWidth placeholder="Search products or codes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)}}/>
//           <Tooltip title="Refresh Data"><IconButton onClick={() => refetch()} disabled={isLoading}><RefreshIcon /></IconButton></Tooltip>
//         </Stack>
//       </Card>

//       {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load products data. Please try refreshing.</Alert>}
      
//       {/* --- Main Content --- */}
//       {isLoading ? (
//         <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
//       ) : isMobile ? (
//         <Box>
//           {filteredData.length > 0 ? (
//             filteredData.map((product) => (
//               <ProductCard key={product.id} product={product} onToggle={handleCardToggle} isExpanded={expandedCards.has(product.id)} />
//             ))
//           ) : (
//             <Paper sx={{ p: 4, textAlign: 'center', background: 'transparent', boxShadow: 'none' }}><Typography>No products found.</Typography></Paper>
//           )}
//         </Box>
//       ) : (
//         <Card>
//           <DataGrid
//             rows={filteredData}
//             columns={columns}
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={[25, 50, 100]}
//             disableRowSelectionOnClick
//             autoHeight
//             sx={{ border: 'none' }}
//           />
//         </Card>
//       )}

//       {/* --- Footer --- */}
//       <Box sx={{ mt: 2, textAlign: 'center' }}>
//         <Typography variant="body2" color="text.secondary">
//           Showing {filteredData.length} products
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// export default Products;
// // src/pages/Products/Products.jsx
// import React, { useState, useEffect, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   Paper,
//   Card,
//   CardContent,
//   Grid,
//   Chip,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Tooltip,
//   Alert,
//   CircularProgress,
//   Stack,
//   Button,
// } from '@mui/material';
// import {
//   Search as SearchIcon,
//   Refresh as RefreshIcon,
//   FilterList as FilterIcon,
//   TrendingUp as TrendingUpIcon,
//   AttachMoney as MoneyIcon,
//   Games as GamesIcon,
// } from '@mui/icons-material';
// import { DataGrid } from '@mui/x-data-grid';
// import { useGetServiceCodesQuery } from '../../store/api/dashboardApi';

// // Utility function to format numbers
// const formatNumber = (num) => {
//   if (num >= 1000000) {
//     return `${(num / 1000000).toFixed(1)}M`;
//   }
//   if (num >= 1000) {
//     return `${(num / 1000).toFixed(1)}K`;
//   }
//   return num?.toString() || '0';
// };

// // Utility function to format currency
// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat('en-KE', {
//     style: 'currency',
//     currency: 'KES',
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   }).format(amount || 0);
// };

// // Product status component
// const ProductStatus = ({ serviceCode, betCount }) => {
//   if (serviceCode === 'all') {
//     return <Chip label="All Services" color="info" size="small" />;
//   }
  
//   const numBets = parseInt(betCount) || 0;
//   if (numBets === 0) {
//     return <Chip label="Inactive" color="default" size="small" />;
//   } else if (numBets < 10) {
//     return <Chip label="Low Activity" color="warning" size="small" />;
//   } else if (numBets < 100) {
//     return <Chip label="Active" color="success" size="small" />;
//   } else {
//     return <Chip label="High Activity" color="primary" size="small" />;
//   }
// };

// const Products = () => {
//   const [searchTerm, setSearchTerm] = useState('');
//   const [paginationModel, setPaginationModel] = useState({
//     page: 0,
//     pageSize: 25,
//   });

//   // Fetch service codes data
//   const {
//     data: serviceCodesData = [],
//     isLoading,
//     error,
//     refetch,
//   } = useGetServiceCodesQuery();

//   // Filter and transform data
//   const filteredData = useMemo(() => {
//     if (!Array.isArray(serviceCodesData)) return [];
    
//     return serviceCodesData
//       .filter(item => {
//         if (!searchTerm) return true;
//         const search = searchTerm.toLowerCase();
//         return (
//           item.label?.toLowerCase().includes(search) ||
//           item.value?.toLowerCase().includes(search)
//         );
//       })
//       .map((item, index) => ({
//         id: index + 1,
//         serviceCode: item.value || 'N/A',
//         productName: item.label || 'Unknown Product',
//         betCount: parseInt(item.bet_count) || 0,
//         totalAmount: parseFloat(item.total_amount) || 0,
//         status: item.value === 'all' ? 'system' : 'active',
//         category: item.value === 'all' ? 'System' : 'Game Service',
//         avgBetAmount: item.bet_count > 0 ? (item.total_amount / item.bet_count) : 0,
//       }));
//   }, [serviceCodesData, searchTerm]);

//   // Calculate summary statistics
//   const summaryStats = useMemo(() => {
//     const activeProducts = filteredData.filter(p => p.serviceCode !== 'all');
//     const totalBets = activeProducts.reduce((sum, p) => sum + p.betCount, 0);
//     const totalRevenue = activeProducts.reduce((sum, p) => sum + p.totalAmount, 0);
//     const avgRevenue = activeProducts.length > 0 ? totalRevenue / activeProducts.length : 0;

//     return {
//       totalProducts: activeProducts.length,
//       totalBets,
//       totalRevenue,
//       avgRevenue,
//     };
//   }, [filteredData]);

//   // DataGrid columns
//   const columns = [
//     {
//       field: 'serviceCode',
//       headerName: 'Service Code',
//       width: 150,
//       renderCell: (params) => (
//         <Box sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
//           {params.value}
//         </Box>
//       ),
//     },
//     {
//       field: 'productName',
//       headerName: 'Product Name',
//       width: 200,
//       flex: 1,
//     },
//     {
//       field: 'category',
//       headerName: 'Category',
//       width: 120,
//     },
//     {
//       field: 'betCount',
//       headerName: 'Total Bets',
//       width: 120,
//       type: 'number',
//       renderCell: (params) => (
//         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//           <GamesIcon fontSize="small" color="action" />
//           {formatNumber(params.value)}
//         </Box>
//       ),
//     },
//     {
//       field: 'totalAmount',
//       headerName: 'Total Revenue',
//       width: 150,
//       type: 'number',
//       renderCell: (params) => (
//         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
//           <MoneyIcon fontSize="small" />
//           {formatCurrency(params.value)}
//         </Box>
//       ),
//     },
//     {
//       field: 'avgBetAmount',
//       headerName: 'Avg Bet Amount',
//       width: 150,
//       type: 'number',
//       renderCell: (params) => formatCurrency(params.value),
//     },
//     {
//       field: 'status',
//       headerName: 'Status',
//       width: 130,
//       renderCell: (params) => (
//         <ProductStatus 
//           serviceCode={params.row.serviceCode} 
//           betCount={params.row.betCount} 
//         />
//       ),
//     },
//   ];

//   return (
//     <Box>
//       {/* Header */}
//       <Box sx={{ mb: 3 }}>
//         <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//           Products & Services
//         </Typography>
//         <Typography variant="body1" color="text.secondary">
//           Manage and monitor your gaming products and service codes
//         </Typography>
//       </Box>

//       {/* Summary Cards */}
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                 <Box sx={{ 
//                   p: 1, 
//                   borderRadius: 2, 
//                   bgcolor: 'primary.light', 
//                   color: 'primary.contrastText' 
//                 }}>
//                   <GamesIcon />
//                 </Box>
//                 <Box>
//                   <Typography variant="h6" component="div">
//                     {summaryStats.totalProducts}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary">
//                     Active Products
//                   </Typography>
//                 </Box>
//               </Box>
//             </CardContent>
//           </Card>
//         </Grid>

//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                 <Box sx={{ 
//                   p: 1, 
//                   borderRadius: 2, 
//                   bgcolor: 'success.light', 
//                   color: 'success.contrastText' 
//                 }}>
//                   <TrendingUpIcon />
//                 </Box>
//                 <Box>
//                   <Typography variant="h6" component="div">
//                     {formatNumber(summaryStats.totalBets)}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary">
//                     Total Bets
//                   </Typography>
//                 </Box>
//               </Box>
//             </CardContent>
//           </Card>
//         </Grid>

//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                 <Box sx={{ 
//                   p: 1, 
//                   borderRadius: 2, 
//                   bgcolor: 'info.light', 
//                   color: 'info.contrastText' 
//                 }}>
//                   <MoneyIcon />
//                 </Box>
//                 <Box>
//                   <Typography variant="h6" component="div">
//                     {formatCurrency(summaryStats.totalRevenue)}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary">
//                     Total Revenue
//                   </Typography>
//                 </Box>
//               </Box>
//             </CardContent>
//           </Card>
//         </Grid>

//         <Grid item xs={12} sm={6} md={3}>
//           <Card>
//             <CardContent>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                 <Box sx={{ 
//                   p: 1, 
//                   borderRadius: 2, 
//                   bgcolor: 'warning.light', 
//                   color: 'warning.contrastText' 
//                 }}>
//                   <MoneyIcon />
//                 </Box>
//                 <Box>
//                   <Typography variant="h6" component="div">
//                     {formatCurrency(summaryStats.avgRevenue)}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary">
//                     Avg Revenue/Product
//                   </Typography>
//                 </Box>
//               </Box>
//             </CardContent>
//           </Card>
//         </Grid>
//       </Grid>

//       {/* Controls */}
//       <Paper sx={{ p: 2, mb: 3 }}>
//         <Stack direction="row" spacing={2} alignItems="center">
//           <TextField
//             placeholder="Search products or service codes..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             size="small"
//             sx={{ flexGrow: 1 }}
//             InputProps={{
//               startAdornment: (
//                 <InputAdornment position="start">
//                   <SearchIcon />
//                 </InputAdornment>
//               ),
//             }}
//           />
//           <Tooltip title="Refresh Data">
//             <IconButton onClick={() => refetch()} disabled={isLoading}>
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Stack>
//       </Paper>

//       {/* Error Alert */}
//       {error && (
//         <Alert severity="error" sx={{ mb: 3 }}>
//           Failed to load products data. Please try refreshing the page.
//         </Alert>
//       )}

//       {/* Data Grid */}
//       <Paper sx={{ height: 600, width: '100%' }}>
//         {isLoading ? (
//           <Box sx={{ 
//             display: 'flex', 
//             justifyContent: 'center', 
//             alignItems: 'center', 
//             height: '100%' 
//           }}>
//             <CircularProgress />
//           </Box>
//         ) : (
//           <DataGrid
//             rows={filteredData}
//             columns={columns}
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={[25, 50, 100]}
//             disableRowSelectionOnClick
//             sx={{
//               '& .MuiDataGrid-row:hover': {
//                 backgroundColor: 'action.hover',
//               },
//             }}
//           />
//         )}
//       </Paper>

//       {/* Footer Info */}
//       <Box sx={{ mt: 2, textAlign: 'center' }}>
//         <Typography variant="body2" color="text.secondary">
//           Showing {filteredData.length} products
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// export default Products;
